// Activity tab — V1 expansion #9 cross-shell audit log surface.
// A-25 audit_events query patterns; per-org scope filter applied MSW-side (admin = all; seller/supply = own org).
// Detay: planning/plans/audit-log-infrastructure-plan.md + G-1 5 yeni event_type kategori extension.
//
// Wave-1 Day 3 i18n wire — CATEGORY_LABEL + filter/limit chrome cross-shell bilingual.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Badge } from '../../components/ui/badge'
import { useT } from '../../i18n'
import { activityFeedI18n } from '../../i18n/shared/activity-feed'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { formatDateTime } from '../../lib/datetime'

export type ActivityCategory =
  | 'iq1'
  | 'iq2'
  | 'iq7'
  | 'supply'
  | 'b2b'
  | 'feedback'
  | 'inventory'
  | 'orders'
  | 'onboarding'
  | 'catalog'
  | 'governance'

type ActivityEntry = {
  id: string
  actor: string
  actor_org_id: string
  category: ActivityCategory
  action: string
  target_type: string
  target_id: string
  target_label: string
  summary: string
  at: string
}

type ActivityResponse = {
  data: ActivityEntry[]
  meta: { total: number; returned: number }
}

const CATEGORY_VARIANT: Record<
  ActivityCategory,
  'default' | 'primary' | 'success' | 'warning' | 'info' | 'neutral'
> = {
  iq1: 'info',
  iq2: 'warning',
  iq7: 'primary',
  supply: 'success',
  b2b: 'info',
  feedback: 'warning',
  inventory: 'neutral',
  orders: 'primary',
  onboarding: 'success',
  catalog: 'primary',
  governance: 'warning',
}

// BUG-20: per-shell category whitelist for filter dropdown. Mirror of the
// server-side SCOPE_ALLOWED_CATEGORIES in handlers.ts. admin = no whitelist.
const SHELL_VISIBLE_CATEGORIES: Record<
  'admin' | 'seller' | 'supply',
  ReadonlyArray<ActivityCategory>
> = {
  admin: [], // empty = all
  seller: ['orders', 'iq7', 'feedback', 'inventory'],
  supply: ['supply', 'iq1', 'feedback'],
}

function fmtTime(iso: string): string {
  return formatDateTime(iso, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Real-mode placeholder for non-admin shells (no seller/supply activity
// endpoint exists yet). Renders the same page chrome with an honest empty feed.
function ActivityFeedUnbacked() {
  const t = useT(activityFeedI18n)
  const scopeLabel = t.scopeOrg
  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-semibold text-[var(--color-text)]">{t.title}</h1>
      <p className="mt-2 text-[var(--color-text-muted)]">{scopeLabel}</p>

      <div className="mt-4 rounded-md border border-sky-200 appearance-dark:border-[var(--color-border)] border-l-4 border-l-sky-500 appearance-dark:border-l-[var(--color-info)] bg-sky-50 appearance-dark:bg-[var(--color-surface-muted)] px-4 py-3 text-xs text-sky-900 appearance-dark:text-[var(--color-text)]">
        <span className="font-medium">{t.auditCalloutLead}</span>{' '}
        {t.auditCalloutBody}
      </div>

      <section className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        <div className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t.empty}
        </div>
      </section>
    </div>
  )
}

export function ActivityFeed({ shell }: { shell: 'admin' | 'seller' | 'supply' }) {
  const t = useT(activityFeedI18n)
  const [category, setCategory] = useState<ActivityCategory | 'all'>('all')
  const [limit, setLimit] = useState(50)

  // Real-engine backing endpoints:
  //   admin  → GET /api/v1/admin/activity   (platform.audit_view; ?scope= filter)
  //   seller → GET /api/v1/seller/activity  (activity.view; D-5 Ring 1 own-org)
  //   supply → (no endpoint yet — needs-BE) → honest empty feed
  // In maket-mode (MSW) all three shells hit /admin/activity?scope= which applies
  // the per-shell whitelist client-side — behavior unchanged.
  const realModeUnbacked = USE_REAL_API && shell === 'supply'

  const { data, isLoading, isError } = useQuery<ActivityResponse>({
    queryKey: ['activity', shell, category, limit],
    enabled: !realModeUnbacked,
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('limit', String(limit))
      if (category !== 'all') params.set('category', category)
      // Seller has a real own-org feed in real-mode; admin + all maket shells use
      // /admin/activity with ?scope= (server/MSW applies the D-5 whitelist).
      let path: string
      if (USE_REAL_API && shell === 'seller') {
        path = `/seller/activity?${params}`
      } else {
        params.set('scope', shell) // BUG-20: pass shell as scope for D-5 isolation
        path = `/admin/activity?${params}`
      }
      const res = await fetch(apiUrl(path))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  if (realModeUnbacked) {
    return <ActivityFeedUnbacked />
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-[var(--color-text-muted)]">{t.loading}</div>
  }
  if (isError || !data) {
    return <div className="p-6 text-sm text-rose-700">{t.error}</div>
  }

  const scopeLabel = shell === 'admin' ? t.scopeAdmin : t.scopeOrg

  const categoryLabels = t.categoryLabels
  const visibleCategoryEntries = (
    Object.keys(categoryLabels) as ActivityCategory[]
  ).filter((key) => {
    const allowed = SHELL_VISIBLE_CATEGORIES[shell]
    return allowed.length === 0 || allowed.includes(key)
  })

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-semibold text-[var(--color-text)]">{t.title}</h1>
      <p className="mt-2 text-[var(--color-text-muted)]">{scopeLabel}</p>

      <div className="mt-4 rounded-md border border-sky-200 appearance-dark:border-[var(--color-border)] border-l-4 border-l-sky-500 appearance-dark:border-l-[var(--color-info)] bg-sky-50 appearance-dark:bg-[var(--color-surface-muted)] px-4 py-3 text-xs text-sky-900 appearance-dark:text-[var(--color-text)]">
        <span className="font-medium">{t.auditCalloutLead}</span>{' '}
        {t.auditCalloutBody}
      </div>

      {/* Filter bar */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <label className="text-xs font-medium text-[var(--color-text-muted)]">{t.filterLabel}</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ActivityCategory | 'all')}
          className="rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1 text-sm"
        >
          <option value="all">{t.allCategories}</option>
          {visibleCategoryEntries.map((key) => (
            <option key={key} value={key}>
              {categoryLabels[key]}
            </option>
          ))}
        </select>
        <label className="text-xs font-medium text-[var(--color-text-muted)] ml-3">{t.limitLabel}</label>
        <select
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value, 10))}
          className="rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1 text-sm"
        >
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
        <span className="ml-auto text-xs text-[var(--color-text-muted)]">
          {data.meta.returned} / {data.meta.total} {t.recordsSuffix}
        </span>
      </div>

      {/* Activity list */}
      <section className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        {data.data.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
            {t.empty}
          </div>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {data.data.map((entry) => (
              <li key={entry.id} className="px-4 py-3 hover:bg-[var(--color-surface-muted)]">
                <div className="flex items-start gap-3">
                  <Badge variant={CATEGORY_VARIANT[entry.category]}>
                    {categoryLabels[entry.category]}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--color-text)]">
                      <span className="font-medium">{entry.actor}</span>{' '}
                      <span className="text-[var(--color-text-muted)]">
                        {entry.action} → {entry.target_type}
                      </span>{' '}
                      <span className="font-medium text-[var(--color-text)]">{entry.target_label}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">{entry.summary}</div>
                  </div>
                  <div className="text-xs text-[var(--color-text-muted)] whitespace-nowrap tabular-nums">
                    {fmtTime(entry.at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

export function ActivityAdmin() {
  return <ActivityFeed shell="admin" />
}
export function ActivitySeller() {
  return <ActivityFeed shell="seller" />
}
export function ActivitySupply() {
  return <ActivityFeed shell="supply" />
}
